import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTablePreferences } from "./utils/useTablePreferences";
import "../styles/ListPage.css";
import "../styles/TestList.css";
import { fetchTestFilter, fetchTestsApi, importTestsCSV } from "../pages/services/apiService";
import { formatDateTime } from "./utils/utility";

const TestList = () => {
  const navigate = useNavigate();

  const [tests, setTests] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [toastMessage, setToastMessage] = useState("");
  const [page, setPage] = useState(0);
  const { rowsPerPage, setRowsPerPage, sortConfig, setSortConfig } = useTablePreferences("table_prefs_tests");
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({ testTitle: "", testSubject: "", testTopic: "", testDuration: "", testModule: "", testSection: "", isAnalysisRequired: "", analysisDuration: "", testType: "", requiredTimer: "" });
  const [loading, setLoading] = useState(false);
  const [firstLoading, setFirstLoading] = useState(true);
  const [importFailModal, setImportFailModal] = useState(false);
  const [importSuccess, setImportSuccess] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [filterOption, setFilterOption] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [importError, setImportError] = useState("");

  const filterChangedRef = useRef(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const message = sessionStorage.getItem("ToastMessage");
    if (message) { setToastMessage(message); sessionStorage.removeItem("ToastMessage"); setTimeout(() => setToastMessage(""), 5000); }
  }, []);

  useEffect(() => {
    const getFilter = async () => {
      try {
        const response = await fetchTestFilter();
        const formattedFilters = Object.keys(response).map((key) => ({
          key,
          label: key.charAt(0).toUpperCase() + key.slice(1),
          options: response[key]
            .filter((item) => item !== null && item !== undefined && item !== "")
            .map((item) => {
              if (typeof item === "object" && item._id) return { label: item.subject || item.module || item.topic || item.section || "N/A", value: String(item._id) };
              if (typeof item === "boolean") return { label: item ? "true" : "false", value: String(item) };
              return { label: String(item), value: String(item) };
            }),
        }));
        setFilterOption(formattedFilters);
      } catch (error) { console.error("Error fetching filter options", error); }
    };
    getFilter();
  }, []);

  const fetchTests = useCallback(async () => {
    if (firstLoading) setLoading(true);
    const queryParams = new URLSearchParams();
    queryParams.append("page", page + 1);
    queryParams.append("pageSize", rowsPerPage);
    if (searchTerm.trim()) queryParams.append("search", searchTerm.trim());

    const filterKeyMap = { testTitle: "testTitle", testSubject: "testSubject", testTopic: "testTopic", testDuration: "testDuration", testModule: "testModule", testSection: "testSection", isAnalysisRequired: "isAnalysisRequired", analysisDuration: "analysisDuration", requiredTimer: "requiredTimer", testType: "testType" };
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value.toString().trim() !== "") queryParams.append(filterKeyMap[key] || key, value.toString().trim());
    });
    if (sortConfig.column && sortConfig.direction) {
      queryParams.append("sortBy", sortConfig.column);
      queryParams.append("order", sortConfig.direction);
    }

    try {
      const response = await fetchTestsApi(queryParams);
      const formattedTests = (response.data || []).map((test) => ({
        ...test,
        testStartFrom: formatDateTime(test.testStartFrom),
        testStartTo: formatDateTime(test.testStartTo),
        testEndTime: formatDateTime(test.testEndTime),
      }));
      setTests(formattedTests);
      setTotalCount(response.pagination?.totalItems || 0);
    } catch (error) { console.error("Error fetching tests:", error); }
    finally {
      if (firstLoading) { setTimeout(() => { setLoading(false); setFirstLoading(false); }, 2000); }
    }
  }, [page, rowsPerPage, searchTerm, filters, sortConfig, firstLoading]);

  useEffect(() => { fetchTests(); }, [fetchTests]);

  useEffect(() => {
    if (filterChangedRef.current) { setPage(0); filterChangedRef.current = false; }
  }, [filters, searchTerm]);

  const handleFilterChange = (key, value) => {
    if (filters[key] !== value) { filterChangedRef.current = true; setFilters((prev) => ({ ...prev, [key]: value })); }
  };

  const onSort = (column) => {
    setPage(0);
    setSortConfig((prev) => {
      if (prev.column === column) {
        if (prev.direction === "asc") return { column, direction: "desc" };
        if (prev.direction === "desc") return { column: null, direction: null };
      }
      return { column, direction: "asc" };
    });
  };

  const thClass = (col) => sortConfig.column === col
    ? (sortConfig.direction === "asc" ? "lp-sort-asc" : "lp-sort-desc")
    : "";

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const handleRowClick = (id) => navigate(`/tests/${id}`);
  const handleAddTest = () => navigate("/tests/add");

  const handleExportCSV = async () => {
    const queryParams = new URLSearchParams();
    queryParams.append("isExport", "true");
    if (searchTerm.trim()) queryParams.append("search", searchTerm.trim());
    Object.entries(filters).forEach(([key, value]) => {
      const v = (value ?? "").toString().trim();
      if (v) queryParams.append(key, v);
    });
    try {
      const blob = await fetchTestsApi(queryParams, { responseType: "blob" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      link.href = url; link.download = `tests-${timestamp}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) { console.error("CSV download error:", error); }
  };

  const handleCSVImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const EXPECTED_HEADERS = ["batchTitle","batchDuration","batchDescription","trainerName","batchTime","batchDays","status","noteTitle","noteDescription","noteLinkOfAttachment","noteDateTime","noteStatus"];
    const reader = new FileReader();
    reader.onload = async (event) => {
      const lines = event.target.result.trim().split("\n");
      const headers = lines[0].split(",").map((h) => h.trim());
      const isHeaderValid = headers.length === EXPECTED_HEADERS.length && headers.every((h, idx) => h.toLowerCase() === EXPECTED_HEADERS[idx].toLowerCase());
      if (!isHeaderValid) { setImportFailModal(true); return; }
      try {
        const response = await importTestsCSV(file);
        if (response.success) { fetchTests(); setImportSuccess(true); setTimeout(() => setImportSuccess(false), 3000); }
        else { setImportError(response.message || "Unknown error occurred"); setImportFailModal(true); }
      } catch (err) { console.error("Import Error:", err); setImportError(err.message || "Server not responding."); setImportFailModal(true); }
      finally { e.target.value = null; }
    };
    reader.readAsText(file);
  };

  const columns = [
    { key: "testTitle", label: "Test Title" },
    { key: "testSubject", label: "Subject" },
    { key: "testTopic", label: "Topic" },
    { key: "testStartFrom", label: "Start From" },
    { key: "testStartTo", label: "Start To" },
    { key: "testEndTime", label: "End Time" },
    { key: "testDuration", label: "Duration" },
    { key: "analysisDuration", label: "Analysis Dur." },
    { key: "testType", label: "Type" },
  ];

  return (
    <div className="lp-page">
      {(toastMessage || importSuccess) && (
        <div className="lp-toast">{importSuccess ? "Test import successful" : toastMessage}</div>
      )}

      {importFailModal && (
        <div className="lp-overlay">
          <div className="lp-modal">
            <h3>Import Failed</h3>
            {importError && <p><strong>Reason:</strong> {importError}</p>}
            <p>Please download the sample CSV and try again.</p>
            <button className="lp-modal-btn" onClick={() => setImportFailModal(false)}>Close</button>
          </div>
        </div>
      )}

      {showImportModal && (
        <div className="lp-overlay" onClick={() => setShowImportModal(false)}>
          <div className="lp-drop-modal" onClick={(e) => e.stopPropagation()}>
            <div
              className={`lp-drop-zone${dragOver ? " lp-drag-active" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault(); setDragOver(false);
                const file = e.dataTransfer.files?.[0];
                if (file) { handleCSVImport({ target: { files: [file] } }); setShowImportModal(false); }
              }}
            >
              <h3>Upload CSV File</h3>
              <p>Drag a file here, or click Browse to select one.</p>
              <div className="lp-drop-actions">
                <button className="lp-btn-primary" onClick={() => fileInputRef.current?.click()}>Browse</button>
                <button className="lp-btn-secondary" onClick={() => {
                  const link = document.createElement("a");
                  link.href = "/sample-test.csv"; link.download = "sample-test.csv";
                  document.body.appendChild(link); link.click(); link.remove();
                }}>Download Sample</button>
              </div>
              <input type="file" accept=".csv" ref={fileInputRef} style={{ display: "none" }} onChange={(e) => { handleCSVImport(e); setShowImportModal(false); }} />
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="lp-header">
        <div className="lp-header-left">
          <h2 className="lp-title">All Tests</h2>
          <span className="lp-count">{totalCount} records</span>
        </div>
        <div className="lp-header-actions">
          <button className="lp-btn-primary" onClick={handleAddTest}>+ Add Test</button>
          <button className="lp-btn-secondary" onClick={handleExportCSV}>Export CSV</button>
          <button className="lp-btn-secondary" onClick={() => setShowImportModal(true)}>Import CSV</button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="lp-toolbar">
        <div className="lp-search-wrap">
          <span className="lp-search-icon">&#128269;</span>
          <input
            type="text"
            placeholder="Search tests…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="lp-search"
          />
        </div>
        <div className="lp-filters">
          {filterOption.map((f) => (
            <select
              key={f.key}
              className="tl-filter-select"
              value={filters[f.key] || ""}
              onChange={(e) => handleFilterChange(f.key, e.target.value)}
            >
              <option value="">{f.label}</option>
              {f.options.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="lp-table-card">
        {loading ? (
          <div className="lp-state"><div className="lp-spinner" /><p>Loading tests…</p></div>
        ) : tests.length === 0 ? (
          <div className="lp-state"><p>No tests found.</p></div>
        ) : (
          <table className="lp-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.key} className={thClass(col.key)} onClick={() => onSort(col.key)}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tests.map((test) => (
                <tr key={test._id} onClick={() => handleRowClick(test._id)}>
                  {columns.map((col) => (
                    <td key={col.key}>{test[col.key] ?? "—"}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!loading && tests.length > 0 && (
        <div className="lp-pagination">
          <button className="lp-page-btn" onClick={() => setPage((p) => Math.max(p - 1, 0))} disabled={page === 0}>← Previous</button>
          <span className="lp-page-info">Page {page + 1} of {totalPages}</span>
          <button className="lp-page-btn" onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))} disabled={page >= totalPages - 1}>Next →</button>
          <div className="lp-page-size">
            <label htmlFor="tl-pageSize">Rows:</label>
            <select id="tl-pageSize" value={rowsPerPage} onChange={(e) => { setPage(0); setRowsPerPage(Number(e.target.value)); }}>
              {[10,20,30,50,75,100].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestList;
